import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Anchor, Badge, Button, Center, Checkbox, Group, Loader, Modal, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ApiError, snapshotApi, type SnapshotItem, type SnapshotOverview, type SnapshotPerson, type SnapshotSummary, undoPaths } from '../api';
import { SNAPSHOT_REASONS, formatBytes, formatDateTime } from '../format';
import { Section } from '../ui/Section';
import { useUndo } from '../ui/undo';

/**
 * "Vorgänge: 79, Abhängigkeiten: 12" for one part, labels in the order of
 * `order` (the current state's) - the database does not keep the stored order.
 */
function counts(summary: SnapshotSummary, key: string, order: SnapshotSummary): string {
  const part = summary[key];
  if (!part) return '–';
  const labels = [...Object.keys(order[key] ?? {}), ...Object.keys(part)].filter((l, i, all) => l in part && all.indexOf(l) === i);
  return labels.length ? labels.map((l) => `${l}: ${part[l]}`).join(', ') : 'enthalten';
}

/** Whether a part's counts differ from now - a hint, not proof: an edited field changes no count. */
function differs(a: SnapshotSummary, b: SnapshotSummary, key: string): boolean {
  const x = a[key] ?? {};
  const y = b[key] ?? {};
  // Only labels both know: an older snapshot may not count everything counted now.
  return Object.keys(x).some((l) => l in y && x[l] !== y[l]);
}

/**
 * The project's snapshots. One is kept whenever the project is opened and
 * something changed, and before every restore; thinned as the database
 * snapshots are. Everyone on the project sees them; restoring a part needs
 * the right to edit it (ADR 0005).
 */
export function SnapshotsPage(): JSX.Element {
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['snapshots', projectId], queryFn: () => snapshotApi.list(projectId) });
  const [chosen, setChosen] = useState<SnapshotItem | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const take = useMutation({
    mutationFn: () => snapshotApi.take(projectId, 'manual'),
    onSuccess: async (r) => {
      setNote(r.created ? 'Snapshot angelegt.' : 'Seit dem letzten Snapshot hat sich nichts geändert – kein neuer nötig.');
      await queryClient.invalidateQueries({ queryKey: ['snapshots', projectId] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => snapshotApi.remove(projectId, id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['snapshots', projectId] }),
  });

  if (data.isPending) return <Center h={200}><Loader /></Center>;
  if (data.isError) return <Alert color="red">Die Snapshots konnten nicht geladen werden.</Alert>;
  const o = data.data;

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        <Section
          title="Snapshots"
          description="Beim Öffnen des Projekts hält PST den Stand fest, sofern sich etwas geändert hat, und ebenso vor jeder Wiederherstellung. Behalten werden alle der letzten 2 Stunden, danach je einer pro Stunde (48), Tag (30), Woche (12) und Monat (24)."
          actions={<Button size="xs" variant="default" loading={take.isPending} onClick={() => take.mutate()}>Jetzt festhalten</Button>}
        >
          {note && <Alert color="gray" py={6} withCloseButton onClose={() => setNote(null)}>{note}</Alert>}
          {remove.isError && <Alert color="red" py={6}>{remove.error instanceof ApiError ? remove.error.message : 'Löschen fehlgeschlagen.'}</Alert>}
          {take.isError && <Alert color="red" py={6}>{take.error instanceof ApiError ? take.error.message : 'Anlegen fehlgeschlagen.'}</Alert>}
          {o.snapshots.length === 0 ? (
            <Text size="sm" c="dimmed">Noch keine Snapshots.</Text>
          ) : (
            <Table.ScrollContainer minWidth={820}>
              <Table verticalSpacing={4} highlightOnHover className="pst-tabular">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={150}>Zeitpunkt</Table.Th>
                    <Table.Th w={230}>Anlass</Table.Th>
                    <Table.Th w={110}>von</Table.Th>
                    <Table.Th>Inhalt</Table.Th>
                    <Table.Th w={80} ta="right">Größe</Table.Th>
                    <Table.Th w={o.canDelete ? 176 : 140} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {o.snapshots.map((s, i) => (
                    <Table.Tr key={s.id}>
                      <Table.Td>
                        <Tooltip label={`behalten: ${s.kept.join(', ')}`} disabled={s.kept.length === 0}>
                          <Text size="sm">{formatDateTime(s.createdAt)}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" style={{ whiteSpace: 'nowrap' }}>{SNAPSHOT_REASONS[s.reason] ?? s.reason}</Text>
                          {i === 0 && <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>neuester</Badge>}
                        </Group>
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{s.by ?? '–'}</Text></Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          {o.parts.filter((p) => s.summary[p.key] && Object.keys(s.summary[p.key]!).length).map((p) => counts(s.summary, p.key, o.current)).join(' · ')}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right"><Text size="sm" c="dimmed">{formatBytes(s.bytes)}</Text></Table.Td>
                      <Table.Td ta="right">
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <Button size="compact-xs" variant="light" onClick={() => setChosen(s)} disabled={!o.parts.some((p) => p.canRestore)}>Wiederherstellen …</Button>
                          {o.canDelete && (
                            <Tooltip label="Snapshot löschen, z. B. wenn gelöschte Personendaten auch hier verschwinden sollen">
                              <ActionIcon size="sm" variant="subtle" color="gray" aria-label="Snapshot löschen" loading={remove.isPending && remove.variables === s.id}
                                onClick={() => { if (window.confirm(`Snapshot vom ${formatDateTime(s.createdAt)} endgültig löschen?`)) remove.mutate(s.id); }}>✕</ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Section>
      </Stack>
      {chosen && <RestoreModal projectId={projectId} overview={o} snapshot={chosen} onClose={() => setChosen(null)} onDone={setNote} />}
    </div>
  );
}

function RestoreModal({ projectId, overview, snapshot, onClose, onDone }: {
  projectId: string;
  overview: SnapshotOverview;
  snapshot: SnapshotItem;
  onClose: () => void;
  onDone: (note: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const undo = useUndo();
  const [parts, setParts] = useState<string[]>([]);
  // Per-person parts (hours): whose rows go back. Nothing is chosen unless asked for.
  const [chosenPeople, setChosenPeople] = useState<Record<string, string[]>>({});
  const people = useQuery({ queryKey: ['snapshot-people', snapshot.id], queryFn: () => snapshotApi.people(projectId, snapshot.id) });
  const personKey = (p: SnapshotPerson): string => p.id ?? 'none';
  // "alle wählen" leaves the hours out: they go back only when chosen person by person.
  const available = overview.parts.filter((p) => p.canRestore && snapshot.summary[p.key] && !p.perPerson);
  const perPersonMissing = parts.some((k) => overview.parts.find((p) => p.key === k)?.perPerson && !(chosenPeople[k]?.length));
  const restore = useMutation({
    mutationFn: () => snapshotApi.restore(projectId, snapshot.id, parts, Object.fromEntries(parts.filter((k) => chosenPeople[k]?.length).map((k) => [k, chosenPeople[k]!]))),
    onSuccess: async (r) => {
      // Earlier steps would act on a state that is gone; the restore itself becomes the one step back.
      undo.clear();
      undo.record({ label: 'Wiederherstellung', method: 'POST', path: undoPaths.restore(projectId, r.before), body: { parts: r.restored, people: r.people, expectState: r.state } });
      const labels = overview.parts.filter((p) => r.restored.includes(p.key)).map((p) => p.label).join(', ');
      onDone(`Wiederhergestellt auf ${formatDateTime(snapshot.createdAt)}: ${labels}. Der Stand davor ist als Snapshot erhalten (Strg+Z / ⌘Z macht es rückgängig).`);
      await queryClient.invalidateQueries();
      onClose();
    },
  });
  return (
    <Modal opened onClose={onClose} title={`Stand vom ${formatDateTime(snapshot.createdAt)} wiederherstellen`} size="lg">
      <Stack gap="sm">
        <Text size="sm">Welche Bereiche sollen auf diesen Stand zurück? Alle anderen bleiben, wie sie sind. Vorher hält PST den jetzigen Stand als Snapshot fest.</Text>
        <Stack gap={10}>
          {overview.parts.map((p) => {
            const inSnap = !!snapshot.summary[p.key];
            const disabled = !p.canRestore || !inSnap;
            const list = people.data?.[p.key] ?? [];
            const box = (
              <Checkbox
                key={p.key}
                disabled={disabled}
                checked={parts.includes(p.key)}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setParts((keys) => (on ? [...keys, p.key] : keys.filter((k) => k !== p.key)));
                  // Switching the hours on offers one's own first - the common case.
                  if (on && p.perPerson && !(chosenPeople[p.key]?.length)) {
                    const own = list.find((x) => x.canRestore);
                    if (own) setChosenPeople((c) => ({ ...c, [p.key]: [personKey(own)] }));
                  }
                }}
                label={<Text size="sm" fw={500}>{p.label}</Text>}
                description={
                  !inSnap ? 'in diesem Snapshot nicht enthalten'
                    : !p.canRestore ? 'keine Berechtigung zum Bearbeiten'
                      : <>Snapshot: {counts(snapshot.summary, p.key, overview.current)}<br />jetzt: {counts(overview.current, p.key, overview.current)}{differs(snapshot.summary, overview.current, p.key) ? ' – verschieden' : ''}{p.perPerson ? <><br />nur die Einträge der gewählten Personen; standardmäßig nicht dabei</> : null}</>
                }
              />
            );
            if (!p.perPerson || !parts.includes(p.key)) return box;
            return (
              <Stack key={p.key} gap={6}>
                {box}
                <Stack gap={4} pl={32}>
                  {people.isPending && <Loader size="xs" />}
                  {list.map((person) => {
                    const k = personKey(person);
                    const on = chosenPeople[p.key]?.includes(k) ?? false;
                    return (
                      <Checkbox
                        key={k}
                        size="xs"
                        disabled={!person.canRestore}
                        checked={on}
                        onChange={(e) => { const c = e.currentTarget.checked; setChosenPeople((all) => ({ ...all, [p.key]: c ? [...(all[p.key] ?? []), k] : (all[p.key] ?? []).filter((x) => x !== k) })); }}
                        label={`${person.name} – Snapshot: ${person.inSnapshot}, jetzt: ${person.now}`}
                        description={person.canRestore ? undefined : 'nur Projektleitung und Administration'}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
        {available.length > 1 && (
          <Anchor size="xs" component="button" type="button" onClick={() => setParts(parts.length === available.length ? [] : available.map((p) => p.key))}>
            {parts.length === available.length ? 'keinen wählen' : 'alle wählen'}
          </Anchor>
        )}
        {perPersonMissing && <Alert color="yellow" py={6}>Zeiterfassung: bitte mindestens eine Person wählen – oder den Bereich abwählen.</Alert>}
        {restore.isError && <Alert color="red" py={6}>{restore.error instanceof ApiError ? restore.error.message : 'Wiederherstellen fehlgeschlagen.'}</Alert>}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button color="red" disabled={parts.length === 0 || perPersonMissing} loading={restore.isPending} onClick={() => restore.mutate()}>Wiederherstellen</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
